import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import { CrewSelfService } from './crew-self.service';
import { AppConfig } from 'src/app/app.config';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false; // Flag to prevent multiple refreshes
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private pendingRequests: Array<Function> = []; // Queue of pending requests  
  private readonly isEncryptionEnabled = AppConfig.settings?.INTRANSIT_ENCRYPTION_ENABLED || true;

  constructor(
    private readonly authService: AuthService, 
    private readonly storageService: StorageService,
    private readonly crewSelfService: CrewSelfService, 
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipIntercept = req.headers.has('skip');    

    // Clone the request to add the token to it
    if(skipIntercept){
        req = req.clone({
            headers: req.headers.delete('skip')
        });
    } else {
        // encrypt the request body if encryption is enabled    
        if(this.isEncryptionEnabled) {
            req = this.crewSelfService.encryptHttpRequestBody(req);
        }

        req = this.addAuthHeader(req);
    }

    return next.handle(req).pipe(
        map((event: any) => { return this.httpResponseHandler(event, req)}),
        catchError((err: HttpErrorResponse) => {
          if (skipIntercept) {
            return this.bypassHttpResponseError(err);
          } else {
            return this.httpResponseErrorHandler(err, req, next);
          }
        })
    );
  }

  private httpResponseHandler(event:any, req: HttpRequest<any>){
    if (event instanceof HttpResponse) {
      if (this.isEncryptionEnabled && event.body?.data) {        
        try {
          const decryptedBody = this.crewSelfService.decryptData(event.body.data, req.url.indexOf('client-configuration') !== -1);
          event.body.data = decryptedBody;
        } catch (error) {
          console.log('AuthInterceptor: Decryption failed:', error);
        }
      }
      return event.clone({ body: event.body });
    }
    return event;
  }

  private httpResponseErrorHandler(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler) {
    if(error.status === 503){
      this.crewSelfService.showMaintenanceAlert();
      return throwError(() => error);
    }
    
    const access_denied = (AppConfig.settings?.ENABLE_ACCESS_DENIED_APPROACH && AppConfig.settings?.MESSAGES_FOR_ACCESS_DENIED_APPROACH?.split("|").indexOf(error.error?.message) === -1) ? true : false;
    if(access_denied){
      this.authService.isLoginInProgress = false;
    }

    if (error.status === 401 && !this.authService.isLoginInProgress) {
      return this.handle401Error(req, next, access_denied);
    }
    return throwError(() => error);
  }
  
  private bypassHttpResponseError(error: HttpErrorResponse) {
    return throwError(() => error);
  }
  
  private handle401Error(req: HttpRequest<any>, next: HttpHandler, access_denied: boolean): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.authService.refreshAccessToken().pipe(
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);

          // Retry the pending requests
          this.pendingRequests.forEach((request) => request());
          this.pendingRequests = [];

          return next.handle(this.addAuthHeader(req)).pipe(
            map((event: any) => { return this.httpResponseHandler(event, req)})
          );
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.pendingRequests = [];
          this.authService.signOut(access_denied);
          return throwError(() => error);
        })
      );
    } else {
      // Queue the request if token is being refreshed
      return new Observable<HttpEvent<any>>((observer) => {
        this.pendingRequests.push(() => {
          next.handle(this.addAuthHeader(req)).pipe(
            map((event: any) => { return this.httpResponseHandler(event, req)})
          ).subscribe(observer);
        });
      });
    }
  }

  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    const enc_token = this.storageService.getDataFromStorage('access_token');
    if(this.authService.isTokenExpired()){
      return req;
    }

    const {access_token} = this.crewSelfService.decryptData(enc_token);
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${access_token}`,
      },
    });
  }
}